import React, { useState } from 'react'
import { Box, IconButton, Typography } from '@mui/material'
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined'

// ── Release notes shown to students ──────────────────────────────────────────
// Bump RELEASE_ID (and refresh the items) each release you want students to see
// again — the dismissal is keyed on RELEASE_ID, so a new id re-shows the card.
const RELEASE_ID = '2026-07-02'
const RELEASE_LABEL = 'July 2026 update'
const RELEASE_HEADLINE = 'Step 3: a clearer way to check your background research'
const RELEASE_ITEMS = [
  'Same goal (stronger research), just clearer — and nothing you wrote is lost.',
  'As you write, use Get AI feedback under each section to check that part is clear and on track.',
  'When your summary is ready, click the new “Check judge-readiness” button at the bottom to see how much of your background could stand up to science-fair judges’ questions — and what to strengthen.',
  'The old “How to improve my background research” button is now part of these two. Give judge-readiness a try!',
]

const STORAGE_KEY = `scholar_whatsnew_dismissed_${RELEASE_ID}`

export default function WhatsNewCard() {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try { return localStorage.getItem(STORAGE_KEY) === '1' } catch { return false }
  })

  if (dismissed) return null

  function dismiss() {
    try { localStorage.setItem(STORAGE_KEY, '1') } catch { /* ignore */ }
    setDismissed(true)
  }

  return (
    <Box
      sx={{
        mb: 3, p: 2.5, borderRadius: 2,
        border: '1px solid', borderColor: 'primary.light',
        bgcolor: 'rgba(15,43,76,0.04)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <CampaignOutlinedIcon color="primary" fontSize="small" />
        <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
          What's new
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          {RELEASE_LABEL}
        </Typography>
        <IconButton size="small" onClick={dismiss} aria-label="Dismiss what's new">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      {RELEASE_HEADLINE && (
        <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>{RELEASE_HEADLINE}</Typography>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {RELEASE_ITEMS.map((item, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 18, mt: '2px', flexShrink: 0 }} />
            <Typography variant="body2" sx={{ lineHeight: 1.55 }}>{item}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  )
}
